import React, { useState } from 'react';
import { Crown, Star, Calendar, CreditCard, X } from '@phosphor-icons/react';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

export const ManageSubscriptionModal = ({ isOpen, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const { t, language } = useLanguage();

  const getLocale = () => {
    const locales = {
      pt: 'pt-PT',
      en: 'en-US',
      es: 'es-ES',
      fr: 'fr-FR',
      de: 'de-DE'
    };
    return locales[language] || 'en-US';
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await api.post('/stripe/create-portal-session');
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(t('checkoutFailed'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasActiveSubscription = user?.subscription_status === 'active';
  const planName = user?.plan === 'vip' ? 'VIP' : user?.plan === 'pro' ? 'Pro' : 'Free';
  const planIcon = user?.plan === 'vip' ? Crown : user?.plan === 'pro' ? Star : null;
  const PlanIcon = planIcon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-accent border border-white/10 rounded-sm max-w-lg w-full">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
        >
          <X weight="bold" className="text-2xl" />
        </button>

        <div className="p-8">
          <h2 className="text-2xl font-heading font-black text-white mb-6">
            {t('manageSubscription')}
          </h2>

          {/* Current Plan */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-white/50 text-sm mb-1">{t('currentPlanLabel')}</div>
                <div className="flex items-center gap-2">
                  {PlanIcon && <PlanIcon weight="fill" className={`${user?.plan === 'vip' ? 'text-primary' : 'text-secondary'} text-2xl`} />}
                  <span className="text-white font-heading font-bold text-2xl">{planName}</span>
                </div>
              </div>
              {hasActiveSubscription && (
                <div className="px-3 py-1 bg-risk-low/20 text-risk-low rounded-full text-xs font-bold uppercase">
                  {t('active')}
                </div>
              )}
            </div>

            {hasActiveSubscription && user?.renewal_date && (
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Calendar weight="duotone" />
                <span>
                  {t('renewsOn')} {new Date(user.renewal_date).toLocaleDateString(getLocale(), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          {hasActiveSubscription ? (
            <div className="space-y-4">
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="w-full px-6 py-3.5 bg-primary text-background font-bold rounded-full hover:bg-primary/90 disabled:opacity-50 transition-all duration-150 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
                    ...
                  </>
                ) : (
                  <>
                    <CreditCard weight="bold" />
                    {t('managePaymentBilling')}
                  </>
                )}
              </button>

              <p className="text-white/40 text-xs text-center">
                {t('updatePaymentInfo')}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-white/60 mb-4">{t('onFreePlan')}</p>
              <a
                href="/pricing"
                className="inline-block px-8 py-3 bg-primary text-background font-bold rounded-full hover:bg-primary/90 transition-all duration-150"
              >
                {t('viewPlans')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
